import React, { useCallback, useState } from 'react';
import { generateGuid } from '../../../../utils/Guid';
import { ITreeNode } from '../../../../components/Tree/CommonTree/CommonTreeNode';
import {
  IWorkflowCatalogNode,
  loadWorkflowCatalogNodes,
  loadTree,
  getDefaultCatalogAction,
  createWorkflowCatalogNode,
} from './WorkflowCatalogUtils';
import { useCreateTreeStore } from '../../../../components/Tree/CommonTree/useTreeStore';
import { Tree } from '../../../../components/Tree/CommonTree/Tree';
import { createEvent } from 'utils/DomUtils';
import { ActionHeader } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { isRightButtonPressed } from 'utils/KeyBoardUtils';
import { WorkflowCatalogNode } from './WorkflowCatalogNode';
import { getWorkflowCatalogPanelRootNodes, usePortalStore } from 'domains/portal/PortalStore';
import { NoWorkflowsMessage } from './NoWorkflowsMessage';

export interface IWorkFlowCatalogProps {
  treeId?: string;
  openAction: (actionHeader: ActionHeader, catalogId: string) => Promise<void>;
  showContextMenu: (node: IWorkflowCatalogNode, element: HTMLElement) => void;
}

export const serviceCatalogEventId = 'ServiceCatalogEvent';

export function WorkflowCatalogTree({ treeId, openAction, showContextMenu }: IWorkFlowCatalogProps) {
  const [id] = useState<string>(treeId || generateGuid());
  const [canRunWorkflow, setCanRunWorkflow] = useState(true);
  const rootNodes = usePortalStore(getWorkflowCatalogPanelRootNodes);

  const resizeLeftPanel = useCallback(() => {
    window.dispatchEvent(createEvent('resizeLeftPanel'));
  }, []);

  const loadNodes = useCallback(async (parent?: ITreeNode<IWorkflowCatalogNode>): Promise<
    ITreeNode<IWorkflowCatalogNode>[]
  > => {
    if (parent?.data) {
      return loadWorkflowCatalogNodes(parent.data);
    } else {
      return loadTree();
    }
  }, []);

  const onMouseDown = useCallback(
    (node: ITreeNode<IWorkflowCatalogNode>, e: React.MouseEvent) => {
      if (node.data && e.nativeEvent.target && isRightButtonPressed(e)) {
        // currentTarget should be the node, not one of its children.
        showContextMenu(node.data, e.currentTarget as HTMLElement);
      }
    },
    [showContextMenu]
  );

  const handleWorkflowCatalogAction = useCallback(
    async (catalogItem: IWorkflowCatalogNode) => {
      const defaultAction = await getDefaultCatalogAction(catalogItem);
      if (defaultAction) {
        await openAction(defaultAction, catalogItem.id);
      }
    },
    [openAction]
  );

  const treeStore = useCreateTreeStore<IWorkflowCatalogNode>({
    treeId: id,
    nodes: rootNodes.map(p => createWorkflowCatalogNode(p)),
    doNotUncache: true,
    loadNodes,
    onNodeSelected: async node => {
      if (node.hasChildren) {
        expandNode(node);
        return;
      }

      if (!canRunWorkflow) {
        return;
      }

      setCanRunWorkflow(false);

      try {
        if (node.data) {
          await handleWorkflowCatalogAction(node.data);
        }
      } finally {
        setCanRunWorkflow(true);
        unselectNode(node);
      }
    },
    onNodeExpanded: () => {
      resizeLeftPanel();
    },
    onNodeFolded: () => {
      resizeLeftPanel();
    },
  });

  const nodes = treeStore(s => s.nodes);
  const isLoading = treeStore(s => s.isLoading);
  const { selectNode, expandNode, foldNode, unselectNode } = treeStore(s => s.actions);

  return !!nodes?.length || isLoading ? (
    <Tree
      classes={{
        root: 'dp-workflow-tree',
      }}
      nodes={nodes}
      onNodeMouseDown={onMouseDown}
      onNodeExpand={expandNode}
      onNodeFold={foldNode}
      onNodeSelect={selectNode}
      nodeContentComponent={WorkflowCatalogNode}
      loading={{
        isLoading,
        height: 300,
      }}
    />
  ) : (
    <NoWorkflowsMessage />
  );
}
